import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { ɵgetDirectives as getDirectives } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

/**
 * Closes ngbTooltip and matTooltip on scroll / touch-move using the libraries'
 * public APIs (hide()/close()). Also blurs the trigger so Material's internal
 * touch/focus tracking is reset and the tooltip can reopen on the next tap.
 */
@Injectable({ providedIn: 'root' })
export class TooltipScrollCloseInitializer implements OnDestroy {
  private isMobileDevice = window.innerWidth <= 767;
  private readonly handler = () => {
    const nodes = document.querySelectorAll<HTMLElement>('[aria-describedby], [ng-reflect-ngb-tooltip], [ng-reflect-message]');
    nodes.forEach(el => {
      // Skip tooltips marked to persist on scroll (e.g., help/info tooltips)
      if (el.hasAttribute('data-tooltip-keep-on-scroll')) {
        return;
      }
      try {
        const dirs = getDirectives(el) as any[];
        for (const d of dirs) {
          if (d instanceof MatTooltip) {
            d.hide(0);
            el.blur();
          } else if (d instanceof NgbTooltip && d.isOpen()) {
            d.close();
          }
        }
      } catch { /* element not part of Angular view */ }
    });
  };

  private readonly resizeHandler = () => {
    const wasMobile = this.isMobileDevice;
    this.isMobileDevice = window.innerWidth <= 767;

    // If device state changed, update event listeners
    if (wasMobile && !this.isMobileDevice) {
      // Changed to desktop: remove listeners
      this.removeListeners();
    } else if (!wasMobile && this.isMobileDevice) {
      // Changed to mobile: add listeners
      this.addListeners();
    }
  };

  constructor(private ngZone: NgZone) {
    if (typeof document === 'undefined') { return; }

    // Only register listeners if initially on mobile
    if (this.isMobileDevice) {
      this.addListeners();
    }

    // Listen for window resize to detect mobile/desktop changes
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.resizeHandler);
    });
  }

  private addListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('scroll', this.handler, { capture: true, passive: true });
      document.addEventListener('touchmove', this.handler, { capture: true, passive: true });
    });
  }

  private removeListeners(): void {
    document.removeEventListener('scroll', this.handler, { capture: true } as any);
    document.removeEventListener('touchmove', this.handler, { capture: true } as any);
  }

  ngOnDestroy(): void {
    this.removeListeners();
    window.removeEventListener('resize', this.resizeHandler);
  }
}

